import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { RouterOutlet, RouterModule, Router, NavigationEnd, ActivatedRoute } from "@angular/router";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { trigger, style, animate, transition, state } from "@angular/animations";
import { QuicklinkModule } from "ngx-quicklink";
import { distinctUntilChanged, filter, forkJoin, map, Observable, switchMap, take } from "rxjs";
import { AuthService } from "./services/auth.service";
import { environment } from "../environments/environment";
import { PublishStateService } from "./services/publish-state.service";
import { CfaStateService } from "./services/cfa-state.service";
import { CfaAuthor } from "./models/cfa-author";
import { NgbDropdownModule, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { FirstSubscribersComponent } from "./components/first-subscribers/first-subscribers.component";
import { ToastContainerComponent } from "./components/toast-container/toast-container.component";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { SubscriptionStateService, UserSubscription } from "./services/subscription-state.service";
import { UtilitiesService } from "./services/utilities.service";
import { setUser as sentrySetUser } from "@sentry/angular";
import { PreloadingManagerService } from "./services/preloading-manager.service";
import { OfflineDetectorComponent } from "./components/offline-detector/offline-detector.component";
import { ImportSubscribersModalComponent } from "./components/import-subscribers-modal/import-subscribers-modal.component";
import { ImportManagerService } from "./services/import-manager.service";
import { FoundingMemberModalComponent } from "./components/founding-member-modal/founding-member-modal.component";
import { GiveawaysUpgradeModalComponent } from "./components/giveaways-upgrade-modal/giveaways-upgrade-modal.component";
import { CfaService, PredefinedSegment } from "./services/cfa.service";
import posthog from 'posthog-js';
import { FeatureBaseService } from "./services/featurebase.service";

@Component({
    selector: "app-root",
    imports: [
        RouterOutlet,
        RouterModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        QuicklinkModule,
        ScrollingModule,
        NgbTooltip,
        NgbDropdownModule,
        FirstSubscribersComponent,
        ToastContainerComponent,
        OfflineDetectorComponent,
        ImportSubscribersModalComponent,
        FoundingMemberModalComponent,
        GiveawaysUpgradeModalComponent
    ],
    templateUrl: "./app.component.html",
    styleUrl: "./app.component.scss",
    standalone: true,
    animations: [
        trigger("sidebarAnimation", [
            transition(":enter", [
                style({ width: 0 }),
                animate("400ms ease-out", style({ width: "240px" }))
            ]),
            transition(":leave", [
                style({ width: "240px" }),
                animate("400ms ease-in", style({ width: 0 }))
            ])
        ]),
        trigger("blockInitialRenderAnimation", [transition(":enter", [])]),
        trigger("rotatingSlideUpFromBottom", [
            state(
                "void",
                style({
                    opacity: 0,
                    transform: "translateY(100%) rotateX(-90deg)"
                })
            ),
            transition(":enter", [
                animate(
                    "500ms ease-out",
                    style({
                        opacity: 1,
                        transform: "translateY(0) rotateX(0deg)"
                    })
                )
            ])
        ]),
        trigger("rotatingSlideDownFromTop", [
            state(
                "void",
                style({
                    opacity: 0,
                    transform: "translateY(-100%) rotateX(90deg)"
                })
            ),
            transition(":enter", [
                animate(
                    "500ms ease-out",
                    style({
                        opacity: 1,
                        transform: "translateY(0) rotateX(0deg)"
                    })
                )
            ])
        ]),
        trigger("collapse", [
            transition(":leave", [
                style({
                    height: "*",
                    overflow: "hidden",
                }),
                animate("400ms ease-out", style({
                    height: "0",
                    overflow: "hidden",
                }))
            ]),
            transition(":enter", [
                style({
                    height: "0",
                    overflow: "hidden",
                }),
                animate("400ms ease-in", style({
                    height: "*",
                    overflow: "hidden",
                }))
            ])
        ]),
    ]
})
export class AppComponent implements OnInit, AfterViewInit {
    cfaAuthor$!: Observable<CfaAuthor | null>;
    cfaAuthor: CfaAuthor | null = null;
    listingUrl: string = "";
    firstSubscribersMode$!: Observable<boolean | null>;
    allowFirstSubscribersModal = true;
    portalUrl$!: Observable<string>;
    subscriptionTrialStatus$!: typeof this._cfaStateService.combinedTrialStatus$;
    giveawaySubscribersCount$!: typeof this._cfaStateService.giveawaySubscribersCount$;

    @ViewChild("importSubscribersModal") importSubscribersModal!: ImportSubscribersModalComponent;
    @ViewChild("foundingMemberModal") foundingMemberModal!: FoundingMemberModalComponent;
    @ViewChild("giveawaysUpgradeModal") giveawaysUpgradeModal!: GiveawaysUpgradeModalComponent;

    constructor(
        private readonly _router: Router,
        private readonly cdr: ChangeDetectorRef,
        private readonly _authService: AuthService,
        public readonly publishStateService: PublishStateService,
        private readonly _cfaStateService: CfaStateService,
        private readonly _featureBaseService: FeatureBaseService,
        private readonly _subscriptionStateService: SubscriptionStateService,
        private readonly _utilitiesService: UtilitiesService,
        private readonly _preloadingManagerService: PreloadingManagerService,
        private readonly _importManagerService: ImportManagerService,
        private readonly _route: ActivatedRoute,
        private readonly _cfaService: CfaService
    ) {
        this.isLoggedIn$ = this._authService.isLoggedIn$;
        this.authCheck$ = this._authService.checkAuthStatusComplete$;
        this.cfaAuthor$ = this._cfaStateService.cfaAuthor$;
        this.portalUrl$ = this._subscriptionStateService.portalUrl$;
        this.subscriptionTrialStatus$ = this._cfaStateService.combinedTrialStatus$;
        this.giveawaySubscribersCount$ = this._cfaStateService.giveawaySubscribersCount$;

        this._authService.isLoggedIn$.pipe(distinctUntilChanged())
            .subscribe(async (isLoggedIn) => {
                if (isLoggedIn && this._authService.user) {
                    try {
                        posthog.identify(this._authService.user.pid, {
                            email: this._authService.user.email
                        });
                    } catch (_) {}
                    if (environment.prod) {
                        sentrySetUser({
                            id: this._authService.user.pid,
                            email: this._authService.user.email
                        });
                    }
                }
                if (environment.prod) {
                    this._featureBaseService.load();
                }
            });

        this.cfaAuthor$.subscribe((cfaAuthor) => {
            this.cfaAuthor = cfaAuthor;
            this.cdr.markForCheck();
        });

        this.cfaAuthor$.pipe(
            filter((cfaAuthor): cfaAuthor is CfaAuthor => !!cfaAuthor),
            take(1)
        ).subscribe((cfaAuthor) => {
            posthog.identify(this._authService.user!.pid, {
                name: cfaAuthor.name,
                amazonProfile: cfaAuthor.link || undefined,
                asin: cfaAuthor.asin || undefined,
                authorSite: cfaAuthor.subdomain ? `https://crewfiction.com/${cfaAuthor.subdomain}` : undefined,
                books: cfaAuthor.dataFetchCompleted ? cfaAuthor.books.length : undefined,
                series: cfaAuthor.dataFetchCompleted ? cfaAuthor.series.length : undefined,
                reviews: cfaAuthor.dataFetchCompleted ? cfaAuthor.reviews.length : undefined
            });
            if (cfaAuthor.author) {
                this.listingUrl = `https://crewfiction.com/authors/${cfaAuthor.author.slug}`;
            }
            if (cfaAuthor.images.profile) {
                this._utilitiesService.preloadImage(cfaAuthor.images.profile.replace("/upload/", "/upload/f_auto,h_100,q_100/"));
            }
        });

        this.firstSubscribersMode$ = this.cfaAuthor$.pipe(
            filter((cfaAuthor): cfaAuthor is CfaAuthor => !!cfaAuthor),
            take(1),
            switchMap((cfaAuthor) => {
                if (cfaAuthor.firstSubscribersConfirmed) {
                    return [false];
                }

                //everything except import
                return forkJoin([
                    this._cfaService.getSubscribersCount([PredefinedSegment.WebsiteSubscribers]),
                    this._cfaService.getSubscribersCount([PredefinedSegment.ReaderMagnetSubscribers]),
                    this._cfaService.getSubscribersCount([PredefinedSegment.BackmatterSubscribers]),
                    this._cfaService.getSubscribersCount([PredefinedSegment.GiveawaySubscribers])
                ]).pipe(
                    map(([website, readerMagnet, backmatter]) => {
                        const totalCount = website.count + readerMagnet.count + backmatter.count;
                        return totalCount >= 3;
                    })
                );
            })
        );

        this._importManagerService.importModal$.subscribe((open) => {
            if (open) {
                this.importSubscribersModal.openImport();
            }
        });
        this._importManagerService.checkPendingTask();
    }

    ngAfterViewInit(): void {
        this.publishStateService.firstPublishLoading$.subscribe((loading) => {
            this.sidebarEnabled = !loading;
            this.fullHeightMode = loading;
            this.cdr.markForCheck();
        });
        // this.publishStateService.firstPublishConfirmed$.subscribe(confirmed => {
        //     this.sidebarEnabled = !confirmed;
        //     this.fullHeightMode = confirmed;
        //     this.cdr.markForCheck();
        // });
        this.publishStateService.firstRing$.subscribe((gotFirstRing) => {
            this.sidebarEnabled = gotFirstRing;
            this.fullHeightMode = !gotFirstRing;
            this.cdr.markForCheck();
        });
        this._computeScrollbarWidth();
    }

    sidebarEnabled = false;
    authorSiteNavCollapsed = false;
    fullHeightMode = false;
    fullScreenMode = false;
    transparentMode = false;
    bgImage!: string;
    isLoggedIn$!: Observable<boolean>;
    authCheck$!: Observable<boolean>;

    isSubscribers: boolean = false;
    isSettings: boolean = false;
    isHome: boolean = false;
    isBooks: boolean = false;
    isCampaigns: boolean = false;

    currentPageBuilderView: typeof this._cfaStateService.pageBuilderView$.value | null = null;

    async ngOnInit() {
        await this._authService.checkAuthStatus();

        this._router.events.subscribe(async (event) => {
            if (event instanceof NavigationEnd) {
                let route = this._route;
                while (route.firstChild) {
                    route = route.firstChild;
                }
                if (event.urlAfterRedirects.includes("/login") ||
                    event.urlAfterRedirects.includes("/onboarding") ||
                    event.urlAfterRedirects.includes("/email-onboarding") ||
                    event.urlAfterRedirects.includes("/desktop") ||
                    event.urlAfterRedirects.match(/\/campaigns\/[0-9]+/) ||
                    event.urlAfterRedirects.match(/\/automations\/[0-9]+/) ||
                    event.urlAfterRedirects.match(/\/giveaways\/[0-9]+/) ||
                    event.urlAfterRedirects.match(/\/giveaways\/.+\/invite/) ||
                    event.urlAfterRedirects.includes("/new/email") ||
                    event.urlAfterRedirects.includes("/new/welcome-sequence") ||
                    event.urlAfterRedirects.includes("/templates") ||
                    event.urlAfterRedirects.includes("/campaigns/resend/")
                ) {
                    this.sidebarEnabled = false;
                } else {
                    this.sidebarEnabled = true;
                }

                if (event.urlAfterRedirects.includes("/login")) {
                    this.transparentMode = true;
                } else {
                    this.transparentMode = false;
                }

                if (event.urlAfterRedirects.includes("/onboarding") ||
                    event.urlAfterRedirects.includes("/desktop") ||
                    event.urlAfterRedirects.match(/\/giveaways\/.+\/invite/)) {
                    this.fullScreenMode = true;
                    this._featureBaseService.hide();
                } else {
                    this.fullScreenMode = false;
                    this._featureBaseService.show();
                    this._checkAndUpdateBgImage();
                    setInterval(() => this._checkAndUpdateBgImage(), 1000 * 60);
                }

                if (event.urlAfterRedirects.includes("/author-page")) {
                    this.currentPageBuilderView = this.currentPageBuilderView || "main";
                    this.authorSiteNavCollapsed = false;
                } else {
                    this.currentPageBuilderView = null;
                    this.authorSiteNavCollapsed = true;
                }

                this.isSubscribers = event.urlAfterRedirects.includes("/subscribers");
                this.isSettings = event.urlAfterRedirects.includes("/settings");
                this.isHome = event.urlAfterRedirects.includes("/home") || event.urlAfterRedirects === "/";
                this.isBooks = event.urlAfterRedirects.includes("/books")|| event.urlAfterRedirects.includes("/edit/");
                this.isCampaigns = event.urlAfterRedirects.includes("/campaigns") || event.urlAfterRedirects.includes("/new/");
                this.cdr.markForCheck();

                this._preloadingManagerService.enablePreloading();
            }
        });

        this._cfaStateService.pageBuilderView$.subscribe((view) => {
            this.currentPageBuilderView = view;
            this.cdr.markForCheck();
            //scroll to top manually on page builder view change
            document.querySelector(".main")?.scrollTo(0, 0);
        });
    }

    private _checkAndUpdateBgImage() {
        this.bgImage = `url("${this._utilitiesService.getBackgroundImage()}")`;
        this.cdr.markForCheck();
    }

    authorSiteToggle() {
        this.authorSiteNavCollapsed = !this.authorSiteNavCollapsed;
        if (!this.authorSiteNavCollapsed && !this.currentPageBuilderView) {
            setTimeout(() => {
                this.changePageBuilderView("main");
            }, 400);
        }
        this.cdr.markForCheck();
    }

    goToListing() {
        if (!this.listingUrl) return;
        window.open(this.listingUrl, "_blank");
    }

    get userInitials(): string {
        const name = this.cfaAuthor?.name || "";
        if (!name) return "";
        const parts = name.split(" ").filter(p => p.length > 0);
        if (parts.length === 0) return "";
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }

    get userEmail() {
        return this._authService.user?.email;
    }

    get authorImage() {
        if (this.cfaAuthor?.images.profile) {
            return this.cfaAuthor.images.profile.replace("/upload/", "/upload/f_auto,h_100,q_100/");
        } else {
            return "";
        }
    }

    comingSoonStates = {
        Insights: false
    };

    toggleComingSoon(which: keyof typeof this.comingSoonStates) {
        if (this.comingSoonStates[which]) return;
        this.comingSoonStates[which] = true;
        this.cdr.markForCheck();
        posthog.capture("feature_interest", {feature: `sidebar-${which}`});
        this._cfaService.featureInterest(`sidebar-${which}`);
        setTimeout(() => {
            this.comingSoonStates[which] = false;
            this.cdr.markForCheck();
        }, 1500);
    }

    openChat() {
        this._featureBaseService.open();
    }

    logout() {
        this._authService.logout();
    }

    onFirstSubsFinish() {
        this.allowFirstSubscribersModal = false;
        this.cdr.markForCheck();
    }

    changePageBuilderView(view: typeof this._cfaStateService.pageBuilderView$.value) {
        this._router.navigate(["/author-page"]);
        this._cfaStateService.pageBuilderView$.next(view);
    }

    async portalClicked() {
        await this._subscriptionStateService.waitForSubscriptionUpdate();
    }

    openUpgradeModal() {
        if (this.foundingMemberModal) {
            this.foundingMemberModal.open();
        }
    }

    openGiveawaysUpgradeModal(): void {
        if (this.giveawaysUpgradeModal) {
            this.giveawaysUpgradeModal.open();
        }
    }

    private _computeScrollbarWidth() {
        // Measures the scrollbar width of the browser and adds it to a css var so we can use even right+left paddings
        const outer = document.createElement('div');
        outer.style.visibility = 'hidden';
        outer.style.overflow = 'scroll'; 
        document.body.appendChild(outer);

        const inner = document.createElement('div');
        outer.appendChild(inner);

        const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

        outer.parentNode?.removeChild(outer);

        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    }
}
